import React, { useState } from 'react';

const COUNTRY_CODES = [
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
];

const PhoneInput = ({ 
  value = '', 
  onChange, 
  placeholder = '0414 434 999',
  className = '',
  required = false,
  disabled = false,
  ...props 
}) => {
  const [countryCode, setCountryCode] = useState('+61');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Parse existing value if provided
  React.useEffect(() => {
    if (value) {
      // Check if value contains a country code
      const matchedCode = COUNTRY_CODES.find(c => value.startsWith(c.code));
      if (matchedCode) {
        setCountryCode(matchedCode.code);
        setPhoneNumber(value.replace(matchedCode.code, '').trim());
      } else {
        // Assume it's an Australian number without country code
        setCountryCode('+61');
        setPhoneNumber(value);
      }
    }
  }, [value]);

  const formatAustralianNumber = (input) => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');
    
    // Format based on length
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 7) {
      return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    } else if (digits.length <= 10) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    } else {
      // Limit to 10 digits
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    let input = e.target.value;
    
    // Remove country code if user types it
    if (input.startsWith(countryCode)) {
      input = input.replace(countryCode, '').trim();
    }

    // Format the number
    let formatted = input;
    if (countryCode === '+61') {
      formatted = formatAustralianNumber(input);
    }
    
    setPhoneNumber(formatted);
    
    // Build full number for parent component
    const fullNumber = formatted ? `${countryCode} ${formatted}` : '';
    onChange?.(fullNumber);
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);
    
    // Update full number with new country code
    const fullNumber = phoneNumber ? `${newCode} ${phoneNumber}` : '';
    onChange?.(fullNumber);
  };

  return (
    <div className={`flex ${className}`}>
      {/* Country Code Dropdown */}
      <div className="relative">
        <select
          value={countryCode}
          onChange={handleCountryCodeChange}
          disabled={disabled}
          className="appearance-none bg-white border border-gray-300 rounded-l-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 disabled:bg-gray-50"
          style={{ minWidth: '100px' }}
        >
          {COUNTRY_CODES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.code}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Phone Number Input */}
      <input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={countryCode === '+61' ? placeholder : 'Phone number'}
        required={required}
        disabled={disabled}
        className="flex-1 border border-l-0 border-gray-300 rounded-r-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 disabled:bg-gray-50"
        {...props}
      />
    </div>
  );
};

export default PhoneInput;